import dagre from '@dagrejs/dagre';
import * as Sentry from '@sentry/react';
import {
  addEdge,
  Background,
  ConnectionLineType,
  Controls,
  Edge,
  MiniMap,
  Node,
  Panel,
  Position,
  ReactFlow,
  useEdgesState,
  useNodesState,
} from '@xyflow/react';
import { useCallback, useContext, useEffect, useState } from 'react';

import '@xyflow/react/dist/style.css';

import {
  AdsAccountKeyword,
  AdsAccountLandingPage,
  AdsCampaign,
  AdsGroup,
  AdsGroupAd,
  AnalyzeAdsAccountRequest,
  DynamicPageVariant,
  WithId,
} from '@superfunnel/types';
import Loader from '@superfunnel/ui-core/Loader';
import Label from '@superfunnel/ui-core/v2/Label';
import Switch from '@superfunnel/ui-core/v2/Switch';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import { UserContext } from '../../contexts/UserContext';
import { analyzeAdsAccount } from '../../services/AdsAccountsService';
import { classNames } from '../../utils';
import { nodeTypes } from './CustomNodes';

const dagreGraph = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));

const nodeWidth = 550;
const nodeHeight = 300;

const getLayoutedElements = (
  nodes: Node[],
  edges: Edge[],
  direction = 'TB',
): { nodes: Node[]; edges: Edge[] } => {
  const isHorizontal = direction === 'LR';
  dagreGraph.setGraph({ rankdir: direction });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const newNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    const newNode: Node = {
      ...node,
      targetPosition: isHorizontal ? Position.Left : Position.Top,
      sourcePosition: isHorizontal ? Position.Right : Position.Bottom,
      // We are shifting the dagre node position (anchor=center center) to the top left
      // so it matches the React Flow node anchor point (top left).
      position: {
        x: nodeWithPosition.x - nodeWidth / 2,
        y: nodeWithPosition.y - nodeHeight / 2,
      },
    };

    return newNode;
  });

  return { nodes: newNodes, edges };
};

export interface DynamicPageMetadata {
  id: string;
  name: string;
  description: string;
  domain: string;
  path: string;
  variants: WithId<DynamicPageVariant>[];
}

interface Props {
  requestId: string;
  adsAccountId: string;
  campaignIds?: string[] | string;
  dynamicPages?: DynamicPageMetadata[];
}

export default function AdsAccountTree({
  requestId,
  adsAccountId,
  campaignIds,
  dynamicPages,
}: Props) {
  const { selectedAccount } = useContext(UserContext);

  const [analyzeReuqest, setAnalyzeRequest] =
    useState<AnalyzeAdsAccountRequest>();
  const [, setError] = useState<string>();
  const [analyzedCampaigns, setAnalyzedCampaigns] = useState<AdsCampaign[]>();
  const [analyzedAdsGroups, setAnalyzedAdsGroups] =
    useState<{ campaignId: string; adsGroups: AdsGroup[] }[]>();
  const [analyzedKeywords, setAnalyzedKeywords] =
    useState<
      { campaignId: string; adGroupId: string; keywords: AdsAccountKeyword[] }[]
    >();
  const [analyzedAds, setAnalyzedAds] =
    useState<{ campaignId: string; adGroupId: string; ads: AdsGroupAd[] }[]>();
  const [analyzedLandingPages, setAnalyzedLandingPages] =
    useState<AdsAccountLandingPage[]>();
  const [showVariants, setShowVariants] = useState<boolean>(false);

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  const onConnect = useCallback(
    (params: any) =>
      setEdges((eds) =>
        addEdge(
          { ...params, type: ConnectionLineType.SmoothStep, animated: true },
          eds,
        ),
      ),
    [setEdges],
  );

  // const onLayout = useCallback(
  //   (direction: any) => {
  //     const { nodes: layoutedNodes, edges: layoutedEdges } =
  //       getLayoutedElements(nodes, edges, direction);

  //     setNodes([...layoutedNodes]);
  //     setEdges([...layoutedEdges]);
  //   },
  //   [nodes, edges, setNodes, setEdges],
  // );

  useEffect(() => {
    setError(undefined);
    if (!selectedAccount) return;
    const fromDate = new Date(Date.now() - 1000 * 60 * 60 * 24 * 30);
    const toDate = new Date();
    analyzeAdsAccount(
      selectedAccount.id,
      requestId,
      fromDate,
      toDate,
      adsAccountId,
      campaignIds
        ? typeof campaignIds === 'string'
          ? [campaignIds]
          : campaignIds
        : undefined,
    ).catch((e) => {
      Sentry.captureException(e);
      setError(e.message);
    });
  }, [requestId, selectedAccount, campaignIds, adsAccountId]);

  useEffect(() => {
    if (!selectedAccount) return;

    const analyzeAccountRequestDocRef = doc(
      selectedAccount.ref,
      'analyzeAccountRequests',
      requestId,
    );

    const unsubscribe = onSnapshot(analyzeAccountRequestDocRef, (event) => {
      const data = event.data();

      if (!data) {
        setAnalyzeRequest(undefined);
        return;
      }

      setAnalyzeRequest(data as AnalyzeAdsAccountRequest);
    });

    const unsubscribeCampaigns = onSnapshot(
      collection(analyzeAccountRequestDocRef, 'analyzedCampaigns'),
      (event) => {
        setAnalyzedCampaigns(
          event.docs.map((doc) => doc.data() as AdsCampaign),
        );
      },
    );

    const unsubscribeAdsGroups = onSnapshot(
      collection(analyzeAccountRequestDocRef, 'analyzedAdsGroups'),
      (event) => {
        setAnalyzedAdsGroups(
          event.docs.map(
            (doc) =>
              doc.data() as { campaignId: string; adsGroups: AdsGroup[] },
          ),
        );
      },
    );

    const unsubscribeKeywords = onSnapshot(
      collection(analyzeAccountRequestDocRef, 'analyzedKeywords'),
      (event) => {
        setAnalyzedKeywords(
          event.docs.map(
            (doc) =>
              doc.data() as {
                campaignId: string;
                adGroupId: string;
                keywords: AdsAccountKeyword[];
              },
          ),
        );
      },
    );

    const unsubscribeAds = onSnapshot(
      collection(analyzeAccountRequestDocRef, 'analyzedAds'),
      (event) => {
        setAnalyzedAds(
          event.docs.map(
            (doc) =>
              doc.data() as {
                campaignId: string;
                adGroupId: string;
                ads: any[];
              },
          ),
        );
      },
    );

    const unsubscribeLandingPages = onSnapshot(
      collection(analyzeAccountRequestDocRef, 'analyzedLandingPages'),
      (event) => {
        setAnalyzedLandingPages(
          event.docs.map((doc) => doc.data() as AdsAccountLandingPage),
        );
      },
    );

    return () => {
      unsubscribe();
      unsubscribeCampaigns();
      unsubscribeAdsGroups();
      unsubscribeKeywords();
      unsubscribeAds();
      unsubscribeLandingPages();
    };
  }, [requestId, selectedAccount]);

  useEffect(() => {
    // Calculate Nodes
    const adsAccountsNodes: Node[] = analyzeReuqest
      ? [
          {
            id: analyzeReuqest.adsAccountId,
            type: 'account',
            data: {
              label: `${analyzeReuqest.adsAccount.name} Google Ads Account`,
              ...analyzeReuqest.adsAccount,
            },
            position: { x: 0, y: 0 },
          },
        ]
      : [
          {
            id: 'loading-account',
            type: 'loading',
            data: {
              label: 'Fetching Google Ads Account',
            },
            position: { x: 0, y: 0 },
          },
        ];

    const campaignNodes: Node[] = analyzeReuqest
      ? analyzedCampaigns?.length
        ? analyzedCampaigns
            .filter((c) => (c.metrics?.impressions ?? 0) > 0)
            .map((campaign) => ({
              id: campaign.id,
              type: 'campaign',
              data: { label: `${campaign.name} Campaign`, ...campaign },
              position: { x: 0, y: 0 },
            }))
        : [
            {
              id: 'loading-campaigns',
              type: 'loading',
              data: {
                label: 'Fetching Campaigns',
                showTargetHandle: true,
              },
              position: { x: 0, y: 0 },
            },
          ]
      : [];

    const adGroupNodes: Node[] =
      analyzeReuqest && analyzedCampaigns?.length && analyzedAdsGroups?.length
        ? analyzedAdsGroups.flatMap(({ adsGroups }) =>
            adsGroups
              .filter((adG) => (adG.metrics?.impressions ?? 0) > 0)
              .map((adsGroup) => ({
                id: adsGroup.id,
                type: 'adgroup',
                data: { label: `${adsGroup.name}`, ...adsGroup },
                position: { x: 0, y: 0 },
              })),
          )
        : [];

    const adNodes: Node[] =
      analyzeReuqest &&
      analyzedCampaigns?.length &&
      analyzedAdsGroups?.length &&
      analyzedAds?.length
        ? analyzedAds.flatMap(({ ads }) => {
            const highestPerformingCR = ads
              .filter((ad) => (ad.metrics?.impressions ?? 0) > 0)
              .reduce((acc, ad) => {
                const conversionRate =
                  (ad.metrics?.conversions ?? 0) / (ad.metrics?.clicks ?? 0);
                return conversionRate > (acc ?? 0) ? conversionRate : acc;
              }, 0);

            return ads
              .filter((ad) => (ad.metrics?.impressions ?? 0) > 0)
              .map((ad) => {
                const cr =
                  (ad.metrics?.conversions ?? 0) / (ad.metrics?.clicks || 1);
                return {
                  id: ad.id,
                  type: 'ad',
                  data: {
                    label: `${(ad as any).headlines?.[0]}`,
                    ...ad,
                    lowPerforming:
                      !!cr &&
                      !!highestPerformingCR &&
                      cr < highestPerformingCR * (1 - threshold),
                    adToPageRelevancy: 2 + Math.floor(Math.random() * 8),
                  },
                  position: { x: 0, y: 0 },
                };
              });
          })
        : [];

    const lpsMapByKey = analyzedLandingPages?.reduce(
      (acc, lp) => {
        acc[lp.domain + lp.path] = lp;
        return acc;
      },
      {} as Record<string, AdsAccountLandingPage>,
    );

    const usedLpsIds = new Set<string>();

    analyzedAds?.forEach(({ ads }) =>
      ads
        .filter((ad) => (ad.metrics?.impressions ?? 0) > 0)
        .forEach((ad) => {
          try {
            if (!('finalUrls' in ad)) return;
            const url = new URL(ad.finalUrls?.[0]);
            const lp = lpsMapByKey?.[`${url.hostname}${url.pathname}`];
            if (lp) {
              usedLpsIds.add(lp.id);
            }
          } catch (e) {
            Sentry.captureException(e);
            console.error(e);
            return null;
          }
        }),
    );

    const lpNodes: Node[] = [];
    if (
      !showVariants &&
      analyzeReuqest &&
      analyzedCampaigns?.length &&
      analyzedAdsGroups?.length &&
      analyzedAds?.length &&
      analyzedLandingPages?.length
    ) {
      lpNodes.push(
        ...analyzedLandingPages
          .filter((lp) => usedLpsIds.has(lp.id))
          .map((lp) => ({
            id: lp.id,
            type: 'landingPage',
            data: { label: `${lp.domain}${lp.path}`, ...lp },
            position: { x: 0, y: 0 },
          })),
      );
    }

    // Calculate Edges
    const campaignEdges: Edge[] = analyzeReuqest
      ? analyzedCampaigns?.length
        ? analyzedCampaigns.map((campaign) => ({
            id: `campaign/${campaign.id}-account`,
            source: campaign.adsAccountId,
            target: campaign.id,
            animated: true,
          }))
        : [
            {
              id: 'loading/campaigns',
              source: analyzeReuqest.adsAccountId,
              target: 'loading-campaigns',
              animated: true,
            },
          ]
      : [];

    const adGroupEdges: Edge[] =
      analyzeReuqest && analyzedCampaigns?.length && analyzedAdsGroups?.length
        ? analyzedAdsGroups.flatMap(({ campaignId, adsGroups }) =>
            adsGroups.map((adsGroup) => ({
              id: `adGroup/${adsGroup.id}-campaign`,
              source: campaignId,
              target: adsGroup.id,
              animated: true,
            })),
          )
        : [];

    const adsToLpsMap = analyzedAds?.flatMap(({ ads }) =>
      ads
        .filter((ad) => (ad.metrics?.impressions ?? 0) > 0)
        .map((ad) => {
          try {
            if (!('finalUrls' in ad)) return null;
            const url = new URL(ad.finalUrls?.[0]);
            if (!url) return null;
            const lp = lpsMapByKey?.[`${url.hostname}${url.pathname}`];
            if (!lp) return null;

            return {
              adId: ad.id,
              lpId: lp.id,
              key: `${url.hostname}${url.pathname}`,
            };
          } catch (e) {
            Sentry.captureException(e);
            console.error(e);
            return null;
          }
        })
        .filter((x) => x)
        .map((x) => x as { adId: string; lpId: string }),
    );

    const adEdges: Edge[] =
      analyzeReuqest &&
      analyzedCampaigns?.length &&
      analyzedAdsGroups?.length &&
      analyzedAds?.length
        ? analyzedAds.flatMap(({ adGroupId, ads }) =>
            ads
              .filter((ad) => (ad.metrics?.impressions ?? 0) > 0)
              .map((ad) => ({
                id: `ad/${ad.id}-adGroup`,
                source: adGroupId,
                target: ad.id,
                animated: true,
              })),
          )
        : [];

    const lpEdges: Edge[] = [];
    if (
      !showVariants &&
      analyzeReuqest &&
      analyzedCampaigns?.length &&
      analyzedAdsGroups?.length &&
      analyzedAds?.length &&
      analyzedLandingPages?.length
    ) {
      lpEdges.push(
        ...Array.from(adsToLpsMap?.values() ?? []).map((lpCon) => ({
          id: `lp/${lpCon.lpId}-${lpCon.adId}-ad`,
          source: lpCon.adId,
          target: lpCon.lpId,
          animated: true,
        })),
      );
    }

    // Calculate Variants Nodes & Edges
    const variantsNodes: Node[] = [];
    const variantsEdges: Edge[] = [];
    if (
      showVariants &&
      analyzeReuqest &&
      analyzedCampaigns?.length &&
      analyzedAdsGroups?.length &&
      analyzedAds?.length &&
      dynamicPages?.length
    ) {
      const { nodes, edges } = calculateEdgesForDynamicPagesVariants(
        analyzedCampaigns,
        analyzedAdsGroups,
        analyzedAds,
        dynamicPages,
      );
      variantsNodes.push(...nodes);
      variantsEdges.push(...edges);
    }

    const newNodes: Node[] = [
      ...adsAccountsNodes,
      ...campaignNodes,
      ...adGroupNodes,
      ...adNodes,
      ...lpNodes,
      ...variantsNodes,
    ];

    const newEdges: Edge[] = [
      ...campaignEdges,
      ...adGroupEdges,
      ...adEdges,
      ...lpEdges,
      ...variantsEdges,
    ];

    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      newNodes,
      newEdges,
      'LR',
    );

    setNodes([...layoutedNodes]);
    setEdges([...layoutedEdges]);
  }, [
    analyzeReuqest,
    analyzedCampaigns,
    analyzedAdsGroups,
    analyzedAds,
    analyzedLandingPages,
    setNodes,
    setEdges,
    analyzedKeywords,
    showVariants,
    dynamicPages,
  ]);

  const threshold = 0.15;

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes} // register custom node types
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onConnect={onConnect}
      connectionLineType={ConnectionLineType.SmoothStep}
      nodesDraggable={false}
      fitView
      style={{ backgroundColor: '#F7F9FB', position: 'static' }}
      minZoom={0.0001}
    >
      <Panel
        position="top-left"
        className={classNames(
          'flex items-center gap-2 rounded-lg bg-white p-2 shadow-md',
          analyzeReuqest?.status !== 'pending' ? 'hidden' : '',
        )}
        hidden={analyzeReuqest?.status !== 'pending'}
      >
        <Loader size={4} className="pb-0.5" />
        <div className="text-sm">
          {(() => {
            if (
              !analyzeReuqest?.completionStatus ||
              !analyzeReuqest.completionStatus.campaigns
            ) {
              return 'Fetching....';
            } else if (!analyzeReuqest.completionStatus.adGroups) {
              return 'Fetching Ad Groups....';
            } else if (!analyzeReuqest.completionStatus.ads) {
              return 'Fetching Ads....';
            } else if (!analyzeReuqest.completionStatus.landingPages) {
              return 'Fetching Landing Pages....';
            } else {
              return 'Analyzing....';
            }
          })()}
        </div>
      </Panel>
      <Panel
        position="top-right"
        className="rounded-lg bg-white p-2 shadow-md"
        hidden={!dynamicPages?.length}
      >
        <div className="flex items-center space-x-3">
          <Switch
            checked={showVariants}
            onCheckedChange={(checked) => {
              setShowVariants(checked);
            }}
          />
          <Label className="text-sm font-medium">Variants</Label>
        </div>
      </Panel>
      <MiniMap />
      <Controls />
      <Background />
    </ReactFlow>
  );
}

function calculateEdgesForDynamicPagesVariants(
  _: AdsCampaign[],
  __: { campaignId: string; adsGroups: AdsGroup[] }[],
  analyzedAds: { campaignId: string; adGroupId: string; ads: AdsGroupAd[] }[],
  dynamicPages: DynamicPageMetadata[],
): { nodes: Node[]; edges: Edge[] } {
  const maxStrengthPerAdId = new Map<string, number>();
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  dynamicPages.forEach((dynamicPage) => {
    dynamicPage.variants
      .sort((a, b) => {
        return (
          Object.keys(b.constraints || {}).length -
          Object.keys(a.constraints || {}).length
        );
      })
      .forEach((variant) => {
        let relevant = false;
        analyzedAds.forEach(({ ads }) => {
          ads.forEach((ad) => {
            const { match, strength } = isAdRelevantToVariant(ad, variant);
            if (!match) return;

            const maxStrength = maxStrengthPerAdId.get(ad.id) || 0;
            if (strength < maxStrength) return;
            if (strength !== maxStrength) {
              maxStrengthPerAdId.set(ad.id, strength);
            }

            console.log(
              'aaaaaaaaaaa',
              variant.id,
              ad.id,
              strength,
              maxStrength,
            );

            edges.push({
              id: `variant/${variant.id}-${ad.id}-ad`,
              source: ad.id,
              target: variant.id,
              animated: true,
            });

            relevant = true;
          });
        });

        if (!relevant) return;

        nodes.push({
          id: variant.id,
          type: 'variant',
          data: { label: variant.name, ...variant },
          position: { x: 0, y: 0 },
        });
      });
  });

  return { nodes, edges };
}

function isAdRelevantToVariant(
  ad: AdsGroupAd,
  variant: DynamicPageVariant,
): { match: boolean; strength: number } {
  const constraints = variant.constraints;
  if (!constraints) return { match: true, strength: 0 };

  if (
    constraints.adGroups?.length &&
    constraints.adGroups.every((adGroup) => adGroup.id !== ad.adGroupId)
  ) {
    return { match: false, strength: 0 };
  }

  if (
    constraints.ads?.length &&
    constraints.ads.every((adC) => adC.id !== ad.id)
  ) {
    return { match: false, strength: 0 };
  }

  return {
    match: true,
    strength: Object.values(constraints).filter((v) => !!v.length).length,
  };
}
